import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import { toast } from "react-toastify";
import swal from "sweetalert";
import FormControl from "../../components/common/FormControl";
import methodModel from "../../methods/methods";
import PageBackButton from "../../components/common/PageBackButton";
import BillingIcon from "../../assets/image/Billing.svg";
import ShippingIcon from "../../assets/image/Shipping.svg";
import WarehouseIcon from "../../assets/image/Warehouse.svg";

function stripHtmlTags(html) {
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || "";
}

// ─── Constants ────────────────────────────────────────────────────────────────
const TAX_RATE_DEFAULT = 6;
const ALLOWED_EXTENSIONS = ["doc", "pdf"];

const generateEstimateNo = () => {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  return `EST-${date}-${Math.floor(1000 + Math.random() * 9000)}`;
};

const generateTrackingNo = () => `TRK-${Date.now()}`;

const buildAddrText = (addr = {}, keys) =>
  keys
    .map((k) => addr[k])
    .filter(Boolean)
    .join(", ");

const expiryDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 15);
  return d.toISOString().split("T")[0];
};

const EMPTY_LINE = () => ({
  DetailType: "SalesItemLineDetail",
  SalesItemLineDetail: {
    ItemRef: { value: "", name: "" },
    UnitPrice: 0,
    Qty: 1,
  },
  Description: "",
  Amount: 0,
  item_name: "",
  db_product_id: "",
});

const INITIAL_FORM = () => ({
  CustomerRef: "",
  BillEmail: "",
  BillAddr: "",
  ShipAddr: "",
  billingAddress: {
    bill_line1: "",
    bill_line2: "",
    bill_city: "",
    bill_country: "",
    bill_postal_code: "",
  },
  shippingAddress: {
    ship_line1: "",
    ship_line2: "",
    ship_city: "",
    ship_country: "",
    ship_postal_code: "",
  },
  ShipVia: "",
  ShipDate: "",
  TrackingNum: generateTrackingNo(),
  DocNumber: generateEstimateNo(),
  TxnDate: new Date().toISOString().split("T")[0],
  ExpirationDate: expiryDate(),
  estimate_date: "",
  AcceptedBy: "",
  AcceptedDate: "",
  store_name: "",
  po_number: "",
  vendor_details: "",
  store_id: "",
  Line: [EMPTY_LINE()],
  tax_type: "automatic",
  tax_rate_id: "",
  // ── Tax identifiers (NEW) ──────────────────────────────────────────────
  tax_code_id: "15", // rate's own id (used as the dropdown value/key)
  tax_rate_ref: "13", // parent tax code id (for TaxRateRef)
  tax_code: "NON", // "TAX" or "NON", derived from percentage
  apply_tax_after_discount: false, // default false
  // ────────────────────────────────────────────────────────────────────────
  tax_percent: 0,
  total_tax: 0,
  discount_value: 0,
  discount_type: "fixed",
  deposit_percent: 0,
  markup_type: "percentage",
  markup_value: 0,
  estimateType: "full",
  partialMode: "fixed",
  partialValue: 0,
  CustomerMemo: "",
  PrivateNote: "",
  file: "",
  TotalAmt: 0,
  total_shipping: 0,
});

// ─── Autofill helper ──────────────────────────────────────────────────────────
const buildAutofillState = (custList, prodDbList, prodList, detail) => {
  const matchedCustomer = detail.email
    ? custList.find(
      (c) =>
        c.PrimaryEmailAddr?.Address?.toLowerCase() ===
        detail.email.toLowerCase(),
    )
    : null;

  const bill = detail.billingAddress || {};
  const ship = detail.shippingAddress || {};

  const mappedLines = (detail.line_items || []).map((item) => {
    const matchedProd = item?.item_name
      ? prodList.find(
        (p) =>
          p.Name?.toLowerCase().trim() ===
          item.item_name?.toLowerCase().trim(),
      )
      : null;

    const matchedDbProd =
      (item.item_id
        ? prodDbList.find(
          (p) => p.Id === item.item_id || p._id === item.item_id,
        )
        : null) ||
      (item.item_name
        ? prodDbList.find(
          (p) =>
            p.productName?.toLowerCase() === item.item_name?.toLowerCase(),
        )
        : null);

    const qty = item.qty || 1;
    const unitPrice = Number(
      matchedDbProd?.price ||
      matchedDbProd?.unitPrice ||
      matchedProd?.UnitPrice ||
      0,
    );
    const description =
      matchedDbProd?.description ||
      matchedDbProd?.Description ||
      matchedProd?.Description ||
      "";

    return {
      DetailType: "SalesItemLineDetail",
      SalesItemLineDetail: {
        ItemRef: {
          value: matchedProd?.Id || "",
          name: matchedProd?.Name || "",
        },
        UnitPrice: unitPrice,
        Qty: qty,
      },
      Description: stripHtmlTags(description),
      Amount: unitPrice * qty,
      item_name: matchedDbProd?.productName || item.item_name || "",
      db_product_id: matchedDbProd?.Id || matchedDbProd?._id || "",
    };
  });

  const newTotal = mappedLines.reduce((acc, l) => acc + l.Amount, 0);

  return (prev) => ({
    ...prev,
    CustomerRef: matchedCustomer?.Id || "",
    BillEmail:
      detail.email ||
      matchedCustomer?.PrimaryEmailAddr?.Address ||
      prev.BillEmail,
    BillAddr:
      buildAddrText(bill, [
        "bill_line1",
        "bill_line2",
        "bill_city",
        "bill_country",
        "bill_postal_code",
      ]) || prev.BillAddr,
    ShipAddr:
      buildAddrText(ship, [
        "ship_line1",
        "ship_line2",
        "ship_city",
        "ship_country",
        "ship_postal_code",
      ]) || prev.ShipAddr,
    billingAddress: {
      bill_line1: bill.bill_line1 || "",
      bill_line2: bill.bill_line2 || "",
      bill_city: bill.bill_city || "",
      bill_country: bill.bill_country || "",
      bill_postal_code: bill.bill_postal_code || "",
    },
    shippingAddress: {
      ship_line1: ship.ship_line1 || "",
      ship_line2: ship.ship_line2 || "",
      ship_city: ship.ship_city || "",
      ship_country: ship.ship_country || "",
      ship_postal_code: ship.ship_postal_code || "",
    },
    ShipVia: detail.ship_method || prev.ShipVia,
    ShipDate: detail.ship_date || prev.ShipDate,
    TrackingNum: detail.tracking_no || prev.TrackingNum,
    DocNumber: detail.estimate_no || prev.DocNumber,
    TxnDate: detail.txn_date || prev.TxnDate,
    ExpirationDate: detail.expiration_date || prev.ExpirationDate,
    estimate_date: detail.estimate_date || prev.estimate_date,
    AcceptedBy: detail.AcceptedBy || prev.AcceptedBy,
    AcceptedDate: detail.AcceptedDate || prev.AcceptedDate,
    store_name: detail.store_name || prev.store_name,
    store_id: detail.store_id || prev.store_id,
    po_number: detail.po_number || prev.po_number,
    vendor_details: detail.vendor_details || prev.vendor_details,
    tax_type: detail.tax_type || prev.tax_type,
    tax_rate_id: detail.tax_rate_id || prev.tax_rate_id,
    // ── Tax identifiers (NEW) ────────────────────────────────────────────
    tax_code_id: detail.tax_code_id || prev.tax_code_id,
    tax_rate_ref: detail.tax_rate_ref || prev.tax_rate_ref,
    tax_code: detail.tax_code || prev.tax_code,
    apply_tax_after_discount:
      detail.apply_tax_after_discount ?? prev.apply_tax_after_discount,
    // ──────────────────────────────────────────────────────────────────────
    tax_percent: detail.tax_percent ?? prev.tax_percent ?? 0,
    total_tax: detail.total_tax ?? prev.total_tax,
    discount_value: detail.discount_value ?? prev.discount_value,
    discount_type: detail.discount_type || prev.discount_type,
    deposit_percent: detail.deposit_percent ?? prev.deposit_percent,
    CustomerMemo: detail.customer_memo || prev.CustomerMemo,
    PrivateNote: detail.private_note || prev.PrivateNote,
    file: detail.file || prev.file,
    Line: mappedLines.length ? mappedLines : prev.Line,
    TotalAmt: mappedLines.length ? newTotal : prev.TotalAmt,
  });
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function GenerateQuote() {
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const { requestQuoteId } = useParams();
  const [allTax, setallTax] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsDb, setProductsDb] = useState([]);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [formError, setFormError] = useState("");
  const detailRef = useRef(null);
  const customersRef = useRef([]);
  const productsRef = useRef([]);
  const productsRefDatabase = useRef([]);

  // ─── Derived totals ─────────────────────────────────────────────────────────
  const subtotalbeforemarkup = formData.TotalAmt;
  const markupAmt =
    formData.markup_type === "percentage"
      ? (Number(formData.markup_value) / 100) * subtotalbeforemarkup
      : Number(formData.markup_value) || 0;
  const subtotal = formData.TotalAmt + markupAmt;
  const taxPercent = Number(formData.tax_percent) || 0;
  const taxAmt = (Number(subtotal) * taxPercent) / 100;
  const discountAmt =
    formData.discount_type === "percent"
      ? (subtotal * Number(formData.discount_value)) / 100
      : Number(formData.discount_value) || 0;

  const estimateTotal = subtotal - discountAmt + taxAmt;
  const depositAmt = (estimateTotal * Number(formData.deposit_percent)) / 100;
  const computedPartialAmt =
    formData.estimateType === "partial" && Number(formData.partialValue) > 0
      ? formData.partialMode === "percentage"
        ? (Number(formData.partialValue) / 100) * estimateTotal
        : Number(formData.partialValue)
      : 0;

  // ─── Data fetching ───────────────────────────────────────────────────────────
  const fetchInitialData = useCallback(async () => {
    loader(true);
    try {
      const [custRes, prodRes, proddb] = await Promise.all([
        ApiClient.post("quickbook/customers", {
          quickbook_token: user?.quick_book_accesToken || "",
          company: user?.quick_book_companyId,
          email: formData.BillEmail,
        }),
        ApiClient.post("quickbook/items/db", {
          quickbook_token: user?.quick_book_accesToken || "",
          company: user?.quick_book_companyId,
        }),
        ApiClient.get("product/listing", {
          status: "active",
          page: "1",
          count: "10000000",
        }),
      ]);

      const custList = custRes?.success ? custRes.data || [] : [];
      const prodList = prodRes?.success
        ? (prodRes.data || []).map((item) => ({
          ...item,
          Name: item?.name,
          Description: stripHtmlTags(item?.description),
          Id: item?.qbId,
          UnitPrice: item?.unitPrice,
        }))
        : [];
      const prodDbList = proddb?.success ? proddb.data?.products || [] : [];

      customersRef.current = custList;
      productsRef.current = prodList;
      productsRefDatabase.current = prodDbList;

      setCustomers(custList);
      setProducts(prodList);
      setProductsDb(prodDbList);

      if (detailRef.current) {
        setFormData(
          buildAutofillState(custList, prodDbList, prodList, detailRef.current),
        );
      }
    } catch (err) {
      console.error(err);
    }
    loader(false);
  }, [formData.BillEmail]);

  const fetchRequestQuoteDetail = async () => {
    if (!requestQuoteId) return;
    loader(true);
    try {
      const res = await ApiClient.get(
        `request-quote/detail?id=${requestQuoteId}`,
      );
      if (res?.success && res?.data) {
        detailRef.current = res.data;
        setFormData((prev) => ({
          ...prev,
          BillEmail:
            res.data?.email || res.data?.customer_email || prev.BillEmail,
        }));
        if (
          customersRef.current.length ||
          productsRefDatabase.current.length ||
          productsRef.current.length
        ) {
          setFormData(
            buildAutofillState(
              customersRef.current,
              productsRefDatabase.current,
              productsRef.current,
              res.data,
            ),
          );
        }
      }
    } catch (err) {
      console.error(err);
      swal({
        icon: "error",
        title: "Error",
        text: "Failed to load request quote details",
        confirmButtonColor: "#115B9D",
      });
    }
    loader(false);
  };
  const getAllTax = () => {
    ApiClient.get("quickbook-tax/tax-codes").then((res) => {
      if (res?.data?.length > 0) {
        const uniqueRates = new Map();

        (res.data || []).forEach((tax) => {
          (tax.rates || []).forEach((rate) => {
            const percentage = Number(rate.percentage);

            if (!uniqueRates.has(percentage)) {
              uniqueRates.set(percentage, {
                tax_rate_ref: tax.id,
                id: rate.id,
                percentage,
                name: `${methodModel.capitalizeFirstLetter(rate?.name)} (${percentage}%)`,
              });
            }
          });
        });

        const sortedRates = Array.from(uniqueRates.values()).sort(
          (a, b) => a.percentage - b.percentage,
        );

        setallTax(sortedRates);
      }
    });
  };

  useEffect(() => {
    if (formData.BillEmail) fetchInitialData();
  }, [formData.BillEmail]);

  useEffect(() => {
    fetchRequestQuoteDetail();
    getAllTax();
  }, []);

  useEffect(() => {
    if (formError) setFormError("");
  }, [formData]);

  // ─── Handlers ────────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomerChange = (e) => {
    const cust = customers.find((c) => c.Id === e.target.value);
    const billKeys = ["Line1", "Line2", "City", "Country", "PostalCode"];
    setFormData((prev) => ({
      ...prev,
      CustomerRef: e.target.value,
      BillEmail: cust?.PrimaryEmailAddr?.Address || "",
      BillAddr: cust ? buildAddrText(cust.BillAddr || {}, billKeys) : "",
      ShipAddr: cust ? buildAddrText(cust.ShipAddr || {}, billKeys) : "",
      billingAddress: {
        bill_line1: cust?.BillAddr?.Line1 || "",
        bill_line2: cust?.BillAddr?.Line2 || "",
        bill_city: cust?.BillAddr?.City || "",
        bill_country: cust?.BillAddr?.Country || "",
        bill_postal_code: cust?.BillAddr?.PostalCode || "",
      },
      shippingAddress: {
        ship_line1: cust?.ShipAddr?.Line1 || "",
        ship_line2: cust?.ShipAddr?.Line2 || "",
        ship_city: cust?.ShipAddr?.City || "",
        ship_country: cust?.ShipAddr?.Country || "",
        ship_postal_code: cust?.ShipAddr?.PostalCode || "",
      },
    }));
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.Line];
    const line = { ...newLines[index] };

    if (field === "db_product_id") {
      const dbProduct = productsDb.find(
        (p) => p.Id === value || p._id === value,
      );
      line.db_product_id = value;
      line.item_name = dbProduct?.productName || "";
      line.SalesItemLineDetail = {
        ...line.SalesItemLineDetail,
        Qty:
          Number(dbProduct?.qty || dbProduct?.quantity) ||
          line.SalesItemLineDetail.Qty,
        UnitPrice: Number(
          dbProduct?.price || dbProduct?.unitPrice || dbProduct?.UnitPrice || 0,
        ),
      };
      line.Description =
        stripHtmlTags(dbProduct?.description) ||
        stripHtmlTags(dbProduct?.Description) ||
        "";
    } else if (field === "ItemRef") {
      const qbProduct = products.find((p) => p.Id === value);
      if (!qbProduct) {
        const dbProduct = productsDb.find(
          (p) => p.Id === line.db_product_id || p._id === line.db_product_id,
        );
        line.SalesItemLineDetail = {
          ...line.SalesItemLineDetail,
          ItemRef: { value: "", name: "" },
          UnitPrice: Number(
            dbProduct?.price ||
            dbProduct?.unitPrice ||
            dbProduct?.UnitPrice ||
            0,
          ),
        };
        line.Description =
          stripHtmlTags(dbProduct?.description) ||
          stripHtmlTags(dbProduct?.Description) ||
          "";
      } else {
        line.SalesItemLineDetail = {
          ...line.SalesItemLineDetail,
          ItemRef: { value: qbProduct.Id, name: qbProduct.Name },
          UnitPrice: qbProduct.UnitPrice || 0,
        };
        line.Description = stripHtmlTags(qbProduct.Description) || "";
      }
    } else if (field === "Qty") {
      line.SalesItemLineDetail = {
        ...line.SalesItemLineDetail,
        Qty: Number(value),
      };
    } else if (field === "UnitPrice") {
      line.SalesItemLineDetail = {
        ...line.SalesItemLineDetail,
        UnitPrice: Number(value),
      };
    } else {
      line[field] = value;
    }

    line.Amount =
      line.SalesItemLineDetail.Qty * line.SalesItemLineDetail.UnitPrice;
    newLines[index] = line;
    setFormData((prev) => ({
      ...prev,
      Line: newLines,
      TotalAmt: newLines.reduce((acc, l) => acc + l.Amount, 0),
    }));
  };

  const addLine = () =>
    setFormData((prev) => ({ ...prev, Line: [...prev.Line, EMPTY_LINE()] }));

  const removeLine = (index) => {
    const newLines = formData.Line.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      Line: newLines,
      TotalAmt: newLines.reduce((acc, l) => acc + l.Amount, 0),
    }));
  };

  // ─── Tax handler (NEW) ───────────────────────────────────────────────────────
  // allTax entries look like: { tax_rate_ref, id, percentage, name }
  // - id            -> rate's own id (stored as tax_code_id, used as dropdown value/key)
  // - tax_rate_ref  -> parent tax code id (stored as tax_rate_ref, used for TaxRateRef)
  // - percentage    -> stored as tax_percent (drives all amount calculations, unchanged)
  // - tax_code      -> "NON" when percentage is 0, otherwise "TAX"
  const handleTaxChange = (selectedRateId) => {
    const selectedTax = allTax.find((t) => t.id === selectedRateId);
    if (!selectedTax) return;
    setFormData((prev) => ({
      ...prev,
      tax_percent: selectedTax.percentage,
      tax_code_id: selectedTax.id,
      tax_rate_ref: selectedTax.tax_rate_ref,
      tax_code: selectedTax.percentage === 0 ? "NON" : "TAX",
    }));
  };

  // ─── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    if (!formData.BillEmail) {
      setFormError("Customer email is required");
      return false;
    }
    if (!formData.tax_code_id) {
      setFormError("Tax is required");
      return false;
    }

    if (!formData.TxnDate) {
      setFormError("Estimate date is required");
      return false;
    }
    if (formData.Line.length === 0) {
      setFormError("Please add at least one line item");
      return false;
    }
    for (let i = 0; i < formData.Line.length; i++) {
      const line = formData.Line[i];
      if (!line.SalesItemLineDetail.ItemRef.value && !line?.item_name) {
        setFormError(`Please select a product for line ${i + 1}`);
        return false;
      }
      if (
        !line.SalesItemLineDetail.UnitPrice ||
        line.SalesItemLineDetail.UnitPrice <= 0
      ) {
        setFormError(`Please enter a rate greater than 0 for product ${i + 1}`);
        return false;
      }
      if (!line.SalesItemLineDetail.Qty || line.SalesItemLineDetail.Qty <= 0) {
        setFormError(`Qty must be greater than 0 for product ${i + 1}`);
        return false;
      }
    }
    if (Number(estimateTotal) <= 0) {
      setFormError("Estimate total must be greater than 0.");
      return false;
    }
    return true;
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const mapLine = (l) => ({
        amount: l.Amount,
        DetailType: "SalesItemLineDetail",
        SalesItemLineDetail: {
          ItemRef: {
            value: l.SalesItemLineDetail.ItemRef.value || "",
            name: l.SalesItemLineDetail.ItemRef.name || l.item_name || "",
          },
          Qty: l.SalesItemLineDetail.Qty,
          UnitPrice: l.SalesItemLineDetail.UnitPrice,
          // NEW — per-line tax code, derived the same way as the header tax code
          TaxCodeRef: { value: formData.tax_code },
        },
        Description: stripHtmlTags(l.Description),
      });

      const privateNoteFields = [
        ["Memo", formData.PrivateNote],
        ["Accepted By", formData.AcceptedBy],
        ["Accepted Date", formData.AcceptedDate],
        ["Shipping Corp", formData.shipping_corp],
        ["Vendor", formData.vendor_details],
        ["StoreName", formData.store_name],
        ["ShipDate", formData.ShipDate],
        ["TrackingNum", formData.TrackingNum],
      ];
      const PrivateNote = privateNoteFields
        .filter(([, v]) => v)
        .map(([label, v]) => `${label}: ${v}`)
        .join("\n");

      const payload = {
        requestQuoteId,
        quickbook_token: user?.quick_book_accesToken || "",
        company: user?.quick_book_companyId,
        CustomerRef: { value: formData.CustomerRef || null },
        BillAddr: {
          Line1: formData.billingAddress?.bill_line1,
          City: formData.billingAddress?.bill_city,
          Country: formData.billingAddress?.bill_country,
          PostalCode: formData.billingAddress?.bill_postal_code,
        },
        ShipAddr: {
          Line1: formData.shippingAddress?.ship_line1,
          City: formData.shippingAddress?.ship_city,
          Country: formData.shippingAddress?.ship_country,
          PostalCode: formData.shippingAddress?.ship_postal_code,
        },
        Line: formData.Line.map(mapLine),
        product_line_items: formData.Line.map((l) => ({
          ...mapLine(l),
          db_product_id: l.db_product_id,
        })),
        ShipMethodRef: { value: "1", name: "Ship via" },
        DocNumber: formData.DocNumber,
        TxnDate: formData.TxnDate,
        ExpirationDate: formData.ExpirationDate,
        CustomerMemo: { value: formData.CustomerMemo },
        // NEW — TxnTaxCodeRef now reflects TAX/NON instead of being hardcoded
        TxnTaxDetail: { TxnTaxCodeRef: { value: formData.tax_code } },
        PrivateNote,
        tax_percent: Number(formData.tax_percent),
        total_tax: taxAmt,
        // NEW — tax identifiers sent through to the backend
        tax_code: formData.tax_code,
        tax_rate_ref: formData.tax_rate_ref,
        tax_code_id: formData.tax_code_id,
        apply_tax_after_discount: formData.apply_tax_after_discount ?? false,
        discount_value: Number(formData.discount_value),
        discount_type: formData.discount_type,
        markup_type: formData.markup_type,
        markup_value: Number(formData.markup_value),
        estimateType: formData.estimateType || "full",
        partialMode: formData.partialMode || "percentage",
        partialValue: Number(formData.partialValue) || 0,
        file: formData.file,
      };
      loader(true);
      const res = await ApiClient.post(
        "generate-quote/create-from-request",
        payload,
      );
      if (res?.success) {
        loader(false);
        const action = await swal({
          icon: "success",
          title: "Estimate Created Successfully!",
          text: "Your estimate has been moved to the Estimates section under the Pending tab.",
          buttons: {
            list: {
              text: "Go Back",
              value: "list",
            },
            view: {
              text: "View Estimate",
              value: "view",
            },
          },
        });

        if (action === "view") {
          navigate(`/estimate/detail/${res?.data?._id}`);
        }

        if (action === "list") {
          navigate(`/order/management?tab=request_quote`);
        }
      }
    } catch (err) {
      swal({
        icon: "error",
        title: "Error",
        text: "An error occurred while creating the estimate",
        confirmButtonColor: "#115B9D",
      });
    }
    loader(false);
  };

  const uploadFile = (e) => {
    const file = e.target.files?.item(0);
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(
        "Please upload a valid file. Accepted formats include document files in DOC and PDF formats.",
      );
      return;
    }
    loader(true);
    ApiClient.postFormFileData("upload/document", { file }).then((res) => {
      if (res.success) setFormData((prev) => ({ ...prev, file: res.fileName }));
      loader(false);
    });
  };

  // ─── Shared classes ──────────────────────────────────────────────────────────
  const inputCls =
    "h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300";

  return (
    <Layout>
      <div className="container mx-auto max-w-6xl py-5">
        <div className="bg-white shadow-lg rounded-2xl border border-gray-100 px-5 sm:px-6 py-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 border-b pb-4">

            <div className="flex items-center gap-4">

              <PageBackButton to="/order/management?tab=request_quote" />

              <div>

                <h1 className="text-xl sm:text-2xl font-bold text-[#115B9D]">
                  Generate Estimate
                </h1>

                <p className="text-sm text-gray-500 mt-1">
                  Create estimate for customer
                </p>

              </div>

            </div>

            <div className="max-[380px]:text-left sm:text-right pl-16 sm:pl-0">

              <p className="text-sm text-gray-500 font-medium">
                Amount (hidden):
              </p>

              <p className="text-xl font-bold text-gray-900">
                ${methodModel.truncateDecimal(formData.TotalAmt || 0)}
              </p>

            </div>

          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Customer row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                  Customer
                </label>
                <div className="capitalize ">
                  <FormControl
                    disabled
                    className="capitalize"
                    placeholder="Select Customer"
                    theme="search"
                    type="select"
                    displayValue="DisplayName"
                    options={customers?.map((c) => ({
                      id: c.Id,
                      DisplayName: c.DisplayName,
                    }))}
                    value={formData.CustomerRef}
                    onChange={(e) =>
                      setFormData({ ...formData, CustomerRef: e })
                    }
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                  Customer Email
                </label>
                <input
                  disabled
                  type="email"
                  name="BillEmail"
                  className={inputCls}
                  value={formData.BillEmail}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Address / Meta Information */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-6">

              {/* ===================== BILL TO ===================== */}
              <div className="md:col-span-1 xl:col-span-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">

                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Billing Address
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Customer billing information
                    </p>
                  </div>

                  <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <img
                      src={BillingIcon}
                      alt="Billing"
                      className="w-7 h-7 object-contain"
                    />
                  </div>
                </div>

                <div className="p-6 space-y-5">

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ship Via
                    </label>

                    <input
                      type="text"
                      name="ShipVia"
                      className={`${inputCls} bg-gray-100 cursor-not-allowed`}
                      value="Ship via"
                      disabled
                      onChange={handleInputChange}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bill To
                    </label>

                    <textarea
                      name="BillAddr"
                      rows="5"
                      className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-700 resize-none outline-none transition-all duration-200 focus:bg-white focus:border-[#115B9D] focus:ring-4 focus:ring-blue-100"
                      value={formData.BillAddr}
                      onChange={handleInputChange}
                    />
                  </div>

                </div>

              </div>

              {/* ===================== SHIP TO ===================== */}

              <div className="md:col-span-1 xl:col-span-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">

                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">

                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      Shipping Address
                    </h3>

                    <p className="text-xs text-gray-500 mt-1">
                      Delivery destination
                    </p>
                  </div>

                  <div className="h-10 w-10 rounded-xl bg-[#115B9D]/10 border border-[#115B9D]/10 flex items-center justify-center">
                    <img
                      src={ShippingIcon}
                      alt="Shipping"
                      className="w-7 h-7 object-contain"
                    />
                  </div>

                </div>

                <div className="p-6 space-y-5">

                  <div className="grid grid-cols-1 gap-4">

                    <div>

                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Estimate No.
                      </label>

                      <input
                        type="text"
                        name="DocNumber"
                        className={inputCls}
                        value={formData.DocNumber}
                        onChange={handleInputChange}
                      />

                    </div>

                    <div>

                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Estimate Date
                      </label>

                      <input
                        type="date"
                        name="TxnDate"
                        className={inputCls}
                        value={formData.TxnDate}
                        onChange={handleInputChange}
                      />

                    </div>

                    <div>

                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ship To
                      </label>

                      <textarea
                        name="ShipAddr"
                        rows="5"
                        className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-700 resize-none outline-none transition-all duration-200 focus:bg-white focus:border-[#115B9D] focus:ring-4 focus:ring-blue-100"
                        value={formData.ShipAddr}
                        onChange={handleInputChange}
                      />

                    </div>

                  </div>

                </div>

              </div>

              {/* ===================== WAREHOUSE ===================== */}

              <div className="md:col-span-2 xl:col-span-4 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">

                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">

                  <div>

                    <h3 className="text-base font-semibold text-gray-900">
                      Warehouse
                    </h3>

                    <p className="text-xs text-gray-500 mt-1">
                      Shipping origin details
                    </p>

                  </div>

                  <div className="h-10 w-10 rounded-xl bg-[#115B9D]/10 border border-[#115B9D]/10 flex items-center justify-center">
                    <img
                      src={WarehouseIcon}
                      alt="Warehouse"
                      className="w-7 h-7 object-contain"
                    />
                  </div>

                </div>

                <div className="p-6 space-y-5">

                  <div className="grid grid-cols-1 gap-4">

                    <div>

                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tracking No.
                      </label>

                      <input
                        type="text"
                        name="TrackingNum"
                        className={inputCls}
                        value={formData.TrackingNum}
                        onChange={handleInputChange}
                      />

                    </div>

                    <div>

                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expiration Date
                      </label>

                      <input
                        type="date"
                        name="ExpirationDate"
                        className={inputCls}
                        value={formData.ExpirationDate}
                        onChange={handleInputChange}
                      />

                    </div>

                    <div>

                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ship From
                      </label>

                      <textarea
                        rows="5"
                        readOnly
                        className="w-full rounded-xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm text-gray-700 resize-none"
                        value={"2000 Avenue P Ste 9\nRiviera Beach, FL 33404"}
                      />

                    </div>

                  </div>

                </div>

              </div>

            </div>

            {/* Store / Vendor */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-gray-50">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                  Store Name
                </label>
                <input
                  type="text"
                  name="store_name"
                  className={inputCls}
                  value={formData.store_name}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                  Vendor Details
                </label>
                <input
                  type="text"
                  name="vendor_details"
                  className={inputCls}
                  value={formData.vendor_details}
                  onChange={handleInputChange}
                />
                <span className="text-[10px] text-gray-600 mt-1">(hidden)</span>
              </div>
            </div>

            {/* Line items */}
            {/* ========================= Product / Service ========================= */}
            <div className="bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden">

              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-gray-50">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Product or Service
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Manage invoice line items and pricing details
                  </p>
                </div>

                {/* <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-50 border border-blue-100">
                  <div className="h-2.5 w-2.5 rounded-full bg-green-500"></div>
                  <span className="text-xs font-medium text-blue-700">
                    Ready
                  </span>
                </div> */}
              </div>

              {/* Table */}
              {/* Desktop Table */}
              <div className="hidden md:block w-full">

                <div className="w-full rounded-2xl border border-gray-200 overflow-hidden bg-white">

                  <table className="w-full table-fixed border-collapse">

                    <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">

                      <tr>

                        <th className="w-[4%] px-2 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          #
                        </th>

                        <th className="w-[20%] px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">

                          <div className="flex flex-col leading-tight">

                            <span>Product</span>

                            <span className="mt-1 text-[9px] font-normal normal-case text-blue-500">
                              Auto-filled from request
                            </span>

                          </div>

                        </th>

                        <th className="w-[22%] px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">

                          <div className="flex flex-col leading-tight">

                            <span>QuickBooks</span>

                            <span className="mt-1 text-[9px] font-normal normal-case text-amber-500">
                              Overrides description &amp; rate
                            </span>

                          </div>

                        </th>

                        <th className="w-[18%] px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Description
                        </th>

                        <th className="w-[8%] px-2 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Qty
                        </th>

                        <th className="w-[10%] px-2 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Rate
                        </th>

                        <th className="w-[12%] px-2 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Amount
                        </th>

                        <th className="w-[8%] px-2 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          Action
                        </th>

                      </tr>

                    </thead>

                    <tbody className="divide-y divide-gray-100 bg-white">

                      {formData.Line.map((line, index) => (

                        <tr
                          key={index}
                          className="group hover:bg-slate-50 transition-all duration-200"
                        >
                          <td className="px-2 py-5 align-top">
                            <div className="flex items-start justify-center mt-2">
                              <div className="h-8 w-8 rounded-xl bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600">
                                {index + 1}
                              </div>
                            </div>
                          </td>

                          {/* ================= Product ================= */}
                          <td className="px-2 py-4 align-top">

                            <div className="space-y-1.5 w-full">

                              {/* <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                Product
                              </label> */}

                              <div className="w-full rounded-lg bg-white focus-within:border-[#115B9D] focus-within:ring-2 focus-within:ring-blue-100 transition-all">

                                <div className="capitalize p-1 w-full">

                                  <FormControl
                                    className="capitalize w-full"
                                    placeholder="Select Product"
                                    theme="search"
                                    type="select"
                                    displayValue="DisplayName"
                                    options={productsDb?.map((c) => ({
                                      id: c.Id || c._id,
                                      DisplayName: c.productName,
                                    }))}
                                    value={line.db_product_id || ""}
                                    onChange={(e) =>
                                      handleLineChange(index, "db_product_id", e)
                                    }
                                  />

                                </div>

                              </div>

                              {line.item_name && (

                                <div className="flex items-center gap-2 rounded-md bg-blue-50 border border-blue-100 px-2 py-1">

                                  <span className="text-[11px]">📦</span>

                                  <p className="flex-1 truncate text-[11px] font-medium text-blue-700">
                                    {line.item_name}
                                  </p>

                                </div>

                              )}

                            </div>

                          </td>

                          {/* ================= QuickBooks ================= */}
                          <td className="px-2 py-4 align-top">

                            <div className="space-y-1.5 w-full">

                              {/* <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                QuickBooks
                              </label> */}

                              <div className="w-full rounded-lg bg-white focus-within:border-[#115B9D] focus-within:ring-2 focus-within:ring-blue-100 transition-all">

                                <div className="capitalize p-1 w-full">

                                  <FormControl
                                    className="capitalize w-full"
                                    placeholder="Select Product"
                                    theme="search"
                                    type="select"
                                    displayValue="DisplayName"
                                    options={products?.map((c) => ({
                                      id: c.Id || c._id,
                                      DisplayName: c.Name,
                                    }))}
                                    value={line.SalesItemLineDetail.ItemRef.value || ""}
                                    onChange={(e) =>
                                      handleLineChange(index, "ItemRef", e)
                                    }
                                  />

                                </div>

                              </div>

                              {!line.SalesItemLineDetail.ItemRef.value ? (

                                <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1">

                                  <span className="block truncate text-[10px] font-medium text-amber-700">
                                    Optional: Overrides Rate
                                  </span>

                                </div>

                              ) : (

                                <div className="rounded-md border border-green-200 bg-green-50 px-2 py-1">

                                  <span className="block truncate text-[10px] font-medium text-green-700">
                                    ✓ {line.SalesItemLineDetail.ItemRef.name}
                                  </span>

                                </div>

                              )}

                            </div>

                          </td>

                          {/* ================= Description ================= */}
                          <td className="px-2 py-4 align-top">

                            <div className="space-y-1.5">

                              {/* <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                Description
                              </label> */}

                              <input
                                type="text"
                                className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-[13px] text-gray-700 outline-none transition-all duration-200 focus:border-[#115B9D] focus:ring-2 focus:ring-blue-100"
                                value={line.Description}
                                onChange={(e) =>
                                  handleLineChange(
                                    index,
                                    "Description",
                                    e.target.value,
                                  )
                                }
                              />

                            </div>

                          </td>

                          {/* ================= Qty ================= */}
                          <td className="px-2 py-4 align-top">

                            <div className="space-y-1.5">

                              {/* <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                Qty
                              </label> */}

                              <input
                                type="number"
                                step="any"
                                min="1"
                                onWheel={(e) => e.target.blur()}
                                className="h-10 w-full rounded-lg border border-gray-300 bg-white px-2 text-center text-[13px] text-gray-700 outline-none transition-all duration-200 focus:border-[#115B9D] focus:ring-2 focus:ring-blue-100"
                                value={line.SalesItemLineDetail.Qty}
                                onChange={(e) => {
                                  let value = e.target.value;
                                  value = value.replace(/-/g, "");
                                  value = value.replace(/e/gi, "");
                                  e.target.value = value;
                                  handleLineChange(index, "Qty", value);
                                }}
                              />

                            </div>

                          </td>

                          {/* ================= Rate ================= */}
                          <td className="px-2 py-4 align-top">

                            <div className="space-y-1.5">

                              {/* <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                Rate
                              </label> */}

                              <div className="relative">

                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                                  $
                                </span>

                                <input
                                  type="number"
                                  step="any"
                                  min="1"
                                  onWheel={(e) => e.target.blur()}
                                  className="h-10 w-full rounded-lg border border-gray-300 bg-white pl-6 pr-2 text-right text-[13px] text-gray-700 outline-none transition-all duration-200 focus:border-[#115B9D] focus:ring-2 focus:ring-blue-100"
                                  value={line.SalesItemLineDetail.UnitPrice || ""}
                                  onChange={(e) => {
                                    let value = e.target.value;
                                    value = value.replace(/-/g, "");
                                    value = value.replace(/e/gi, "");
                                    e.target.value = value;
                                    handleLineChange(index, "UnitPrice", value);
                                  }}
                                />

                              </div>

                            </div>

                          </td>

                          {/* ================= Amount ================= */}
                          <td className="px-2 py-4 align-top">

                            <div className="space-y-1.5">

                              {/* <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                Amount
                              </label> */}

                              <div className="flex h-10 items-center justify-end rounded-lg border border-green-200 bg-green-50 px-2">

                                <span className="truncate text-[13px] font-semibold text-green-700">
                                  ${methodModel.truncateDecimal(line.Amount || 0)}
                                </span>

                              </div>

                            </div>

                          </td>

                          {/* ================= Delete ================= */}
                          <td className="px-1 align-top">

                            <div className="flex justify-center pt-4 w-fit">

                              <button
                                type="button"
                                onClick={() => removeLine(index)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-500 transition-all duration-200 hover:bg-red-500 hover:border-red-500 hover:text-white"
                              >

                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M3 6h18" />
                                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                  <path d="M8 6V4c0-1 1-2 2-2h4c0 1 2 2v2" />
                                </svg>

                              </button>

                            </div>

                          </td>

                        </tr>

                      ))}

                    </tbody>

                  </table>

                </div>

              </div>

              {/* ================= Mobile Cards ================= */}
              <div className="block md:hidden p-4 space-y-5">

                {formData.Line.map((line, index) => (

                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
                  >

                    {/* Card Header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">

                      <div className="flex items-center gap-3">

                        <div className="h-9 w-9 rounded-xl bg-[#115B9D] text-white flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </div>

                        <div>
                          <h3 className="font-semibold text-gray-800">
                            Product #{index + 1}
                          </h3>

                          <p className="text-xs text-gray-500">
                            Product Details
                          </p>
                        </div>

                      </div>

                      <button
                        type="button"
                        onClick={() => removeLine(index)}
                        className="h-10 w-10 rounded-xl bg-red-50 border border-red-200 text-red-500 flex items-center justify-center"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3 6h18" />
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                        </svg>
                      </button>

                    </div>

                    {/* Card Body */}

                    <div className="p-4 space-y-5">

                      {/* Product */}

                      <div>

                        <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">
                          Product
                        </label>

                        <div className="capitalize">

                          <FormControl
                            className="capitalize"
                            placeholder="Select Product"
                            theme="search"
                            type="select"
                            displayValue="DisplayName"
                            options={productsDb?.map((c) => ({
                              id: c.Id || c._id,
                              DisplayName: c.productName,
                            }))}
                            value={line.db_product_id || ""}
                            onChange={(e) =>
                              handleLineChange(index, "db_product_id", e)
                            }
                          />

                        </div>

                        {line.item_name && (

                          <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-3 py-1">

                            <span>📦</span>

                            <span className="text-xs text-blue-700 font-medium">
                              {line.item_name}
                            </span>

                          </div>

                        )}

                      </div>

                      {/* QuickBooks Product */}

                      <div>

                        <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">
                          QuickBooks Product
                        </label>

                        <div className="capitalize">

                          <FormControl
                            className="capitalize"
                            placeholder="Select QuickBook Product"
                            theme="search"
                            type="select"
                            displayValue="DisplayName"
                            options={products?.map((c) => ({
                              id: c.Id || c._id,
                              DisplayName: c.Name,
                            }))}
                            value={line.SalesItemLineDetail.ItemRef.value || ""}
                            onChange={(e) =>
                              handleLineChange(index, "ItemRef", e)
                            }
                          />

                        </div>

                        {!line.SalesItemLineDetail.ItemRef.value ? (

                          <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1">

                            <span>⚠</span>

                            <span className="text-xs text-amber-700">
                              Optional : Overrides Rate
                            </span>

                          </div>

                        ) : (

                          <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-green-50 border border-green-200 px-3 py-1">

                            <span>✓</span>

                            <span className="text-xs text-green-700">
                              {line.SalesItemLineDetail.ItemRef.name}
                            </span>

                          </div>

                        )}

                      </div>

                      {/* Description */}

                      <div>

                        <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">
                          Description
                        </label>

                        <input
                          type="text"
                          className="w-full h-11 rounded-xl border border-gray-300 px-4 text-sm outline-none focus:border-[#115B9D] focus:ring-4 focus:ring-blue-100"
                          value={line.Description}
                          onChange={(e) =>
                            handleLineChange(
                              index,
                              "Description",
                              e.target.value
                            )
                          }
                        />

                      </div>

                      {/* Qty & Rate */}

                      <div className="grid grid-cols-2 gap-4">

                        {/* Qty */}

                        <div>

                          <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">
                            Qty
                          </label>

                          <input
                            type="number"
                            step="any"
                            min="1"
                            onWheel={(e) => e.target.blur()}
                            className="w-full h-11 rounded-xl border border-gray-300 px-4 text-right text-sm outline-none focus:border-[#115B9D] focus:ring-4 focus:ring-blue-100"
                            value={line.SalesItemLineDetail.Qty}
                            onChange={(e) => {

                              let value = e.target.value;
                              value = value.replace(/-/g, "");
                              value = value.replace(/e/gi, "");

                              e.target.value = value;

                              handleLineChange(
                                index,
                                "Qty",
                                value
                              );

                            }}
                          />

                        </div>

                        {/* Rate */}

                        <div>

                          <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">
                            Rate
                          </label>

                          <div className="relative">

                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                              $
                            </span>

                            <input
                              type="number"
                              step="any"
                              min="1"
                              onWheel={(e) => e.target.blur()}
                              className="w-full h-11 rounded-xl border border-gray-300 pl-8 pr-4 text-right text-sm outline-none focus:border-[#115B9D] focus:ring-4 focus:ring-blue-100"
                              value={line.SalesItemLineDetail.UnitPrice || ""}
                              onChange={(e) => {

                                let value = e.target.value;

                                value = value.replace(/-/g, "");
                                value = value.replace(/e/gi, "");

                                e.target.value = value;

                                handleLineChange(
                                  index,
                                  "UnitPrice",
                                  value
                                );

                              }}
                            />

                          </div>

                        </div>

                      </div>

                      {/* Amount */}

                      <div className="rounded-2xl bg-green-50 border border-green-200 p-4">

                        <div className="flex items-center justify-between">

                          <span className="text-sm font-medium text-gray-600">
                            Amount
                          </span>

                          <span className="text-xl font-bold text-green-700">
                            ${methodModel.truncateDecimal(line.Amount || 0)}
                          </span>

                        </div>

                      </div>

                    </div>

                  </div>

                ))}

              </div>

              {/* ================= Footer ================= */}

              <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between border-t border-gray-200 bg-gray-50 px-6 py-5">

                <div className="text-sm text-gray-500">
                  Add multiple products or services to your estimate.
                </div>

                <button
                  type="button"
                  onClick={addLine}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#115B9D] px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-[#0E4C84] hover:shadow-lg active:scale-95"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 5v14" />
                    <path d="M5 12h14" />
                  </svg>

                  Add Product / Service

                </button>

              </div>

            </div>

            {/* Bottom section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-8 lg:gap-12">
              {/* Memos */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Customer payment options
                  </h3>
                  <textarea
                    name="CustomerMemo"
                    rows="2"
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.CustomerMemo}
                    onChange={handleInputChange}
                    placeholder="Tell your customer how you want to get paid."
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
                    Memo on statement (hidden)
                  </label>
                  <textarea
                    name="PrivateNote"
                    rows="2"
                    className="w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                    value={formData.PrivateNote}
                    onChange={handleInputChange}
                    placeholder="This memo will not show up on your estimate, but will appear on the statement."
                  />
                </div>
              </div>

              {/* Totals */}
              <div className="space-y-4">
                <div className="bg-gray-50/50 rounded-2xl space-y-4 p-5 border border-gray-100">
                  {/* Base cost */}
                  <div className="flex gap-2 justify-between items-center text-sm">
                    <span className="text-gray-500 font-medium">Base Cost</span>
                    <span className="text-gray-900 font-semibold">
                      ${methodModel.truncateDecimal(subtotalbeforemarkup || 0)}
                    </span>
                  </div>

                  {/* Markup — editable */}
                  <div className="flex gap-2 justify-between items-center text-sm">
                    <span className="text-gray-500 font-medium shrink-0">
                      Markup
                    </span>
                    <div className="flex flex-wrap justify-end items-center gap-2 ml-auto">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        onWheel={(e) => e.target.blur()}
                        name="markup_value"
                        className="w-[80px] p-1.5 text-right border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                        value={formData.markup_value}
                        onFocus={() => {
                          if (formData.markup_value === 0) {
                            handleInputChange({
                              target: {
                                name: "markup_value",
                                value: "",
                              },
                            });
                          }
                        }}
                        onInput={(e) => {
                          let value = e.target.value;
                          value = value.replace(/-/g, "");
                          value = value.replace(/e/gi, "");
                          e.target.value = value;
                          handleInputChange({
                            target: {
                              name: "markup_value",
                              value,
                            },
                          });
                        }}
                      />
                      <div className="select_control">
                        <FormControl
                          placeholder="Select Markup Type"
                          theme="search"
                          type="select"
                          options={[
                            { name: "$", id: "fixed" },
                            { name: "%", id: "percentage" },
                          ]}
                          value={formData.markup_type}
                          onChange={(e) =>
                            setFormData({ ...formData, markup_type: e })
                          }
                        />
                      </div>
                      <span className="text-gray-900 font-semibold w-20 text-right">
                        +${methodModel.truncateDecimal(markupAmt || 0)}
                      </span>
                    </div>
                  </div>

                  {/* Subtotal */}
                  <div className="flex gap-2 justify-between items-center text-sm border-t border-gray-200 pt-2">
                    <span className="text-gray-500 font-medium">Subtotal</span>
                    <span className="text-gray-900 font-semibold">
                      ${methodModel.truncateDecimal(subtotal || 0)}
                    </span>
                  </div>

                  {/* Tax — now a live, editable dropdown bound to the rate's id */}
                  <div className="flex gap-2 justify-between items-center text-sm">
                    <span className="text-gray-500 font-medium shrink-0">
                      Sales tax
                    </span>
                    <div className="flex flex-wrap justify-end items-center gap-2 ml-auto">
                      <div className="min-w-[160px]">
                        <FormControl
                          placeholder="Select tax"
                          theme="search"
                          type="select"
                          displayValue="name"
                          options={allTax}
                          value={formData.tax_code_id}
                          onChange={handleTaxChange}
                        />
                      </div>
                      <span className="text-gray-900 font-semibold w-20 text-right">
                        +${methodModel.truncateDecimal(taxAmt || 0)}
                      </span>
                    </div>
                  </div>

                  {/* Discount */}
                  <div className="flex gap-2 justify-between items-center text-sm">
                    <span className="text-gray-500 font-medium shrink-0">
                      Discount
                    </span>
                    <div className="flex flex-wrap justify-end items-center gap-2 ml-auto">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        onWheel={(e) => e.target.blur()}
                        onFocus={() => {
                          if (formData.discount_value === 0) {
                            handleInputChange({
                              target: {
                                name: "discount_value",
                                value: "",
                              },
                            });
                          }
                        }}
                        name="discount_value"
                        className="w-[80px] p-1.5 text-right border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                        value={formData.discount_value}
                        onChange={handleInputChange}
                      />
                      <div className="select_control">
                        <FormControl
                          placeholder="Select Discount Type"
                          theme="search"
                          type="select"
                          options={[
                            { name: "$", id: "fixed" },
                            { name: "%", id: "percent" },
                          ]}
                          value={formData.discount_type}
                          onChange={(e) =>
                            setFormData({ ...formData, discount_type: e })
                          }
                        />
                      </div>
                      <span className="text-gray-900 font-semibold w-20 text-right">
                        -${methodModel.truncateDecimal(discountAmt || 0)}
                      </span>
                    </div>
                  </div>

                  {/* Payment Type */}
                  <div className="flex gap-2 justify-between items-center text-sm">
                    <span className="text-gray-500 font-medium shrink-0">
                      Payment type
                    </span>
                    <div className="flex flex-wrap justify-end items-center gap-2 ml-auto">
                      <div className="select_control w-[160px]">
                        <FormControl
                          placeholder="Select Payment Type"
                          theme="search"
                          type="select"
                          options={[
                            { name: "Full Payment", id: "full" },
                            { name: "Partial Payment", id: "partial" },
                          ]}
                          value={formData.estimateType || "full"}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              estimateType: e,
                              partialValue: e === "full" ? 0 : formData.partialValue,
                            })
                          }
                        />
                      </div>
                      <span className="text-gray-900 font-semibold w-20 text-right"></span>
                    </div>
                  </div>

                  {/* Partial Payment Settings */}
                  {formData.estimateType === "partial" && (
                    <div className="flex gap-2 justify-between items-center text-sm animate-in fade-in duration-200">
                      <span className="text-gray-500 font-medium shrink-0">
                        Partial payment
                      </span>
                      <div className="flex flex-wrap justify-end items-center gap-2 ml-auto">
                        <input
                          type="number"
                          min="0"
                          step="any"
                          onWheel={(e) => e.target.blur()}
                          onFocus={() => {
                            if (formData.partialValue === 0) {
                              handleInputChange({
                                target: {
                                  name: "partialValue",
                                  value: "",
                                },
                              });
                            }
                          }}
                          name="partialValue"
                          className="w-[80px] p-1.5 text-right border rounded-xl text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300"
                          value={formData.partialValue}
                          onChange={handleInputChange}
                        />
                        <div className="select_control">
                          <FormControl
                            placeholder="Select Mode"
                            theme="search"
                            type="select"
                            options={[
                              { name: "$", id: "fixed" },
                              { name: "%", id: "percentage" },
                            ]}
                            value={formData.partialMode}
                            onChange={(e) =>
                              setFormData({ ...formData, partialMode: e })
                            }
                          />
                        </div>
                        <span className="text-[#115B9D] font-semibold w-20 text-right">
                          ${methodModel.truncateDecimal(computedPartialAmt || 0)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Total */}
                  <div className="flex gap-2 justify-between items-center pt-2 border-t border-gray-200">
                    <span className="text-base font-bold text-gray-900">
                      Estimate total
                    </span>
                    <span className="text-2xl font-bold text-[#115B9D]">
                      ${methodModel.truncateDecimal(estimateTotal || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-4 mt-12 pt-6 border-t">
              {formError && (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-red-600 border border-red-100 bg-red-50/50 rounded-xl animate-in fade-in duration-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {formError}
                </div>
              )}
              <div className="flex gap-2 flex-wrap items-center justify-end">
                <button
                  type="button"
                  onClick={() =>
                    navigate("/order/management?tab=request_quote")
                  }
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
